/**
 * Frontend mirror of the backend's business-day classification (see
 * restaurant-analytics-backend/src/utils/businessDay.ts).
 *
 * The frontend only ever needs to know *which calendar date* to preselect as
 * "today" when seeding a report's default date range — it always sends plain
 * "YYYY-MM-DD" dates to the backend, which owns the actual open/close boundary
 * math. So only the date-classification logic is mirrored here, not the full
 * boundary calculation.
 */

export interface BusinessHours {
  openTime: string
  closeTime: string
  /** IANA zone the open/close times are expressed in. Defaults to Asia/Kolkata. */
  timezone?: string
}

export const DEFAULT_BUSINESS_HOURS: BusinessHours = { openTime: '00:00', closeTime: '23:59' }

const DEFAULT_TIMEZONE = 'Asia/Kolkata'

export function crossesMidnight(hours: BusinessHours): boolean {
  return hours.closeTime <= hours.openTime
}

/** Calendar date ("YYYY-MM-DD") and time ("HH:mm") of `instant` in `timezone`. */
function zonedParts(instant: Date, timezone: string): { date: string; time: string } {
  const parts: Record<string, string> = {}
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).formatToParts(instant)) {
    if (p.type !== 'literal') parts[p.type] = p.value
  }
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` }
}

function shiftDateStr(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

/**
 * Which business day (as "YYYY-MM-DD", in the restaurant's timezone) `instant` currently belongs to.
 * Mirrors the backend's getBusinessDateForInstant classification exactly.
 */
export function getBusinessDateForInstant(instant: Date, hours: BusinessHours): string {
  const { date: todayStr, time: hhmm } = zonedParts(instant, hours.timezone || DEFAULT_TIMEZONE)
  if (!crossesMidnight(hours)) return todayStr
  if (hhmm >= hours.openTime) return todayStr
  return shiftDateStr(todayStr, -1)
}

/** Which business day "now" belongs to, as "YYYY-MM-DD" in the restaurant's timezone. */
export function getCurrentBusinessDate(hours: BusinessHours = DEFAULT_BUSINESS_HOURS): string {
  return getBusinessDateForInstant(new Date(), hours)
}
